/**
 * Leaderboard Service
 * API calls for fetching leaderboard data
 */
import { get } from './common';
import { LeaderboardEntry, LeaderboardScope } from '../Types/leaderboard';

const leaderboardEndpoint = 'api/Leaderboards';

/**
 * Format a date for API query parameters
 */
const formatDateParam = (date: Date): string => {
	return date.toISOString();
};

/**
 * Get global leaderboard (all users)
 */
export async function getGlobalLeaderboard(limit: number): Promise<LeaderboardEntry[]> {
	const endpoint = `${leaderboardEndpoint}/GetGlobalLeaderboard?limit=${limit}`;
	const result = await get<LeaderboardEntry[]>(endpoint);
	return result || [];
}

/**
 * Get global leaderboard filtered by date range
 */
export async function getGlobalLeaderboardBetweenDates(
	limit: number,
	startTime: Date,
	endTime: Date
): Promise<LeaderboardEntry[]> {
	const endpoint = `${leaderboardEndpoint}/GetGlobalLeaderboardBetweenDates?limit=${limit}&startTime=${formatDateParam(startTime)}&endTime=${formatDateParam(endTime)}`;
	const result = await get<LeaderboardEntry[]>(endpoint);
	return result || [];
}

/**
 * Get main unit leaderboard (users in same main unit)
 */
export async function getMainUnitLeaderboard(limit: number): Promise<LeaderboardEntry[]> {
	const endpoint = `${leaderboardEndpoint}/GetMainUnitLeaderboard?limit=${limit}`;
	const result = await get<LeaderboardEntry[]>(endpoint);
	return result || [];
}

/**
 * Get main unit leaderboard filtered by date range
 */
export async function getMainUnitLeaderboardBetweenDates(
	limit: number,
	startTime: Date,
	endTime: Date
): Promise<LeaderboardEntry[]> {
	const endpoint = `${leaderboardEndpoint}/GetMainUnitLeaderboardBetweenDates?limit=${limit}&startTime=${formatDateParam(startTime)}&endTime=${formatDateParam(endTime)}`;
	const result = await get<LeaderboardEntry[]>(endpoint);
	return result || [];
}

/**
 * Get unit leaderboard (users in same unit)
 */
export async function getUnitLeaderboard(limit: number): Promise<LeaderboardEntry[]> {
	const endpoint = `${leaderboardEndpoint}/GetUnitLeaderboard?limit=${limit}`;
	const result = await get<LeaderboardEntry[]>(endpoint);
	return result || [];
}

/**
 * Get unit leaderboard filtered by date range
 */
export async function getUnitLeaderboardBetweenDates(limit: number, startTime: Date, endTime: Date): Promise<LeaderboardEntry[]> {
	const endpoint = `${leaderboardEndpoint}/GetUnitLeaderboardBetweenDates?limit=${limit}&startTime=${formatDateParam(startTime)}&endTime=${formatDateParam(endTime)}`;
	const result = await get<LeaderboardEntry[]>(endpoint);
	return result || [];
}

/**
 * Get leaderboard based on scope and optional date range
 */
export async function getLeaderboard(
	scope: LeaderboardScope,
	limit: number,
	fromDate?: Date | null,
	toDate?: Date | null
): Promise<LeaderboardEntry[]> {
	const hasDateRange = fromDate && toDate;

	switch (scope) {
		case 'global':
			return hasDateRange ? getGlobalLeaderboardBetweenDates(limit, fromDate, toDate) : getGlobalLeaderboard(limit);
		case 'main-unit':
			return hasDateRange ? getMainUnitLeaderboardBetweenDates(limit, fromDate, toDate) : getMainUnitLeaderboard(limit);
		case 'unit':
			return hasDateRange ? getUnitLeaderboardBetweenDates(limit, fromDate, toDate) : getUnitLeaderboard(limit);
		default:
			return getGlobalLeaderboard(limit);
	}
}
